const { actionUtils, moduleRegistration } = require('@sstdev/lib_metadata-config');
const namespaceRelation = require('./namespaceRelation');
const relationProperties = require('./relationProperties');
const ForeignKeyRegistry = require('./ForeignKeyRegistry');
const JsonSchemaRegistry = require('./JsonSchemaRegistry');
const blockGroups = require('./generatedBlockDefinitions/blockGroups');

//transforms metadata into dataDictionary
/**
 *
 * @param {*} redactedMetadata
 * @param {'USER'| 'ADMIN'| 'SUPPORT'| 'DEVICE'| 'DEITY'| 'SYSTEM'} role
 * @param {function} notificationHandler
 * @returns
 */
function transform(redactedMetadata, role, notificationHandler) {
    if (!['USER', 'ADMIN', 'SUPPORT', 'DEVICE', 'DEITY', 'SYSTEM'].includes(role)) {
        throw new Error(`To Transform metadata, role should be a system role. Not ${role}`);
    }
    if (notificationHandler == null) {
        throw new Error('A notificationHandler parameter must be provided for the data dictionary transform function.');
    }
    //anything in the tree is fair game
    //start with the basics: namespace/relations
    const emptyDataDictionary = namespaceRelation.extract(redactedMetadata, role);
    //now start collecting relation Properties
    const { dataDictionary, _meta } = relationProperties.extract(
        emptyDataDictionary,
        redactedMetadata,
        role,
        notificationHandler
    );
    return { ...dataDictionary, _meta };
}

/**
 * TypeBox/JSON Schema 2020-12 Type Definitions
 *
 * @typedef {object} TypeDefinition - A TypeBox schema with JSON Schema 2020-12 fields and custom x-* extensions
 * @property {string} type - JSON Schema type (e.g., 'string', 'number', 'object', 'array', 'boolean')
 * @property {string} title - Human-readable title
 * @property {string} [x-id] - Unique identifier for this schema element
 * @property {string} [x-dataType] - Original data type from block metadata
 * @property {string} [x-propertyName] - Property name in the data model
 * @property {boolean} [x-optional] - Whether this property is optional
 * @property {boolean} [x-editable] - Whether this property can be edited by users
 * @property {number} [minLength] - Minimum string length (for type: 'string')
 * @property {number} [maxLength] - Maximum string length (for type: 'string')
 * @property {number} [minimum] - Minimum value (for type: 'number'/'integer')
 * @property {number} [maximum] - Maximum value (for type: 'number'/'integer')
 * @property {string} [format] - Standard JSON Schema format (e.g., 'email', 'date-time', 'uri')
 * @property {string} [x-format] - Custom format for non-standard types (e.g., 'currency', 'image')
 * @property {Object.<string, TypeDefinition>} [properties] - Nested properties for type: 'object'
 * @property {string[]} [required] - Required property names for type: 'object'
 * @property {boolean} [additionalProperties] - Whether additional properties are allowed
 * @property {TypeDefinition} [items] - Item schema for type: 'array'
 * @property {*} [default] - Default value
 *
 * @typedef {Object.<string, TypeDefinition>} Relation
 * @typedef {Object.<string, Relation>} Namespace
 * @typedef {Object.<string, Namespace>} DataDictionary
 */

/**
 * extract the dataDictionary from an hNode tree
 * @param {object} hNode
 * @param {{
 *     allFeatureFlags?:Array<string>,
 *     "identity:role"?:{title:string}
 * }} context
 * @returns {[DataDictionary?, TypeDefinition?]} array with 2 values: optionally the dataDictionary as constructed deeper in the tree,
 * and optionally any type definition as defined deeper.
 */
function extract(hNode, context = {}, parentAccessRestrictions) {
    let foreignKeyRegistry;
    let jsonSchemaRegistry;

    if (!context.registerForeignKey) {
        //add all the callbacks to the context, if necessary
        foreignKeyRegistry = new ForeignKeyRegistry();
        jsonSchemaRegistry = new JsonSchemaRegistry();

        context.registerForeignKey = foreignKeyRegistry.registerForeignKey;
        context.extractFromChild = extract;

        // JSON Schema registry methods
        context.registerSchema = jsonSchemaRegistry.register;
        context.registerRelation = jsonSchemaRegistry.registerRelation;
        context.jsonSchemaRegistry = jsonSchemaRegistry;
    }

    //construct the tree off of this node
    const { hNodeTypeGroup } = hNode;
    let subDataDictionary = {};
    let partialTypeDefinition = {};
    // figure out access restrictions for this node
    const accessRestrictions = actionUtils.getNodeAccessRestrictions(hNode, context, parentAccessRestrictions);
    // if the context role or feature flags does not result in access to this (or sub) component
    // don't include any part of it in the data dictionary.
    if (accessRestrictions.hidden) return [{}, {}];

    // Find the subDataDictionary and partialTypeDefinition for this node
    const asDataDictionary = blockGroups[`${hNodeTypeGroup}s`]?.[hNode.hNodeType]?.asDataDictionary;
    if (asDataDictionary != null) {
        [subDataDictionary = {}, partialTypeDefinition = {}] = asDataDictionary(
            hNode,
            context,
            parentAccessRestrictions,
            accessRestrictions
        );
    } else {
        if (hNode.children) {
            [subDataDictionary = {}, partialTypeDefinition = {}] = hNode.children?.reduce(
                ([sdd, ptd], child) => {
                    const [_subDataDictionary = {}, _partialTypeDefinition = {}] = extract(
                        child,
                        context,
                        accessRestrictions
                    );
                    // Use relation-aware merge for subDataDictionary to properly combine required arrays
                    return [
                        moduleRegistration.mergeSubDataDictionaries(sdd, _subDataDictionary),
                        Object.assign(ptd, _partialTypeDefinition)
                    ];
                },
                [{}, {}]
            ) ?? [{}, {}];
        }
    }

    // if we created the foreignKeyRegistry at this level
    // then this is the root of the tree. Trigger updates for any referenced values
    foreignKeyRegistry?.updateWithFinalDataDictionary(subDataDictionary);
    return [subDataDictionary, partialTypeDefinition];
}

module.exports = { transform, ForeignKeyRegistry, extract };
